import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import { toTransportCategory, TransportCategory } from "./transport-category";

export const ObjectPreferenceCodec = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    category: t.string,
  }),
  t.partial({
    dispatchRequestDuration: t.number,
    dispatchRequestEnabled: t.boolean,
    dispatchRequestAcceptTimeLimit: t.number,
    dispatchRequestAutoAccept: t.boolean,
    dispatchPerformPastAllowance: t.number,
    dispatchPerformFutureAllowance: t.number,
    invoiceUseTicketNumber: t.boolean,
    invoiceForceDeliveryTicketNumber: t.boolean,
    invoiceDeliveryTicketNumberInDesc: t.boolean,
    invoiceUseContainerInLieuOfWellInDesc: t.boolean,
    invoiceShowComments: t.boolean,
    invoiceShowTicketDocumentation: t.boolean,
    invoicePreferTicketImage: t.boolean,
    invoiceBusinessValidatorInDesc: t.boolean,
    invoiceWorkOrderNumberInDesc: t.boolean,
    invoiceBreakOnPickupMonth: t.boolean,
    invoiceBreakOnDisposalMonth: t.boolean,
    reportDefaultTime: t.number,
    reportDefaultWeekDay: t.string,
    reportDefaultTruckCompanyId: t.string,
    reportDefaultOperatorId: t.string,
    reportDefaultTransportRateModelId: t.string,
    reportDefaultDisposalRateModelId: t.string,
    reportDefaultReportTime: t.string,
    reportDefaultReportTimeField: t.string,
    reportDefaultSortByPickupFreightType: t.boolean,
    reportDefaultSortByDeliveryFreightType: t.boolean,
    reportDefaultLockDatabaseTransactions: t.boolean,
    reportDefaultPrimaryCompanyReport: t.boolean,
    reportDefaultReportConfig: t.string,
    codingDefaultAuthorizerId: t.string,
    codingDefaultEngineerId: t.string,
    codingAllowWaterTypeOverrideAfe: t.boolean,
    codingAllowSplit: t.boolean,
    codingAutoCode: t.boolean,
    productionAllocationType: t.string,
    productionAllocationMethod: t.string,
    productionAllocationWeekDay: t.string,
    productionAllocationRetroChunkCount: t.number,
    productionAllocationRetroTimeGroup: t.string,
    activityPeriodOperatorCodedValidation: t.boolean,
    activityPeriodRequireOperatorValidated: t.boolean,
    scadaQuantityAutoChangeEnabled: t.boolean,
    scadaQuantityAutoChangeScadaDiffThreshold: t.number,
    scadaQuantityAutoChangeQuantityDiffThreshold: t.number,
    scadaQuantityAutoChangeOverrideThreshold: t.number,
    serviceEventMinGeofenceEventDuration: t.number,
    dispatchCarryoverEnabled: t.boolean,
    dispatchCarryoverAfterHours: t.number,
    dispatchCarryoverMax: t.number,
    dispatchCarryoverExcludedDefault: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ObjectPreferenceArrayCodec = t.array(
  ObjectPreferenceCodec
);

export type ObjectPreference = t.TypeOf<typeof ObjectPreferenceCodec>;
export type ObjectPreferenceArray = t.TypeOf<typeof ObjectPreferenceArrayCodec>;

@Exclude()
export class ObjectPreferenceEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  dispatchRequestDuration?: number;

  @Expose()
  dispatchRequestEnabled?: boolean;

  @Expose()
  dispatchRequestAcceptTimeLimit?: number;

  @Expose()
  dispatchRequestAutoAccept?: boolean;

  @Expose()
  dispatchPerformPastAllowance?: number;

  @Expose()
  dispatchPerformFutureAllowance?: number;

  @Expose()
  invoiceUseTicketNumber?: boolean;

  @Expose()
  invoiceForceDeliveryTicketNumber?: boolean;

  @Expose()
  invoiceDeliveryTicketNumberInDesc?: boolean;

  @Expose()
  invoiceUseContainerInLieuOfWellInDesc?: boolean;

  @Expose()
  invoiceShowComments?: boolean;

  @Expose()
  invoiceShowTicketDocumentation?: boolean;

  @Expose()
  invoicePreferTicketImage?: boolean;

  @Expose()
  invoiceBusinessValidatorInDesc?: boolean;

  @Expose()
  invoiceWorkOrderNumberInDesc?: boolean;

  @Expose()
  invoiceBreakOnPickupMonth?: boolean;

  @Expose()
  invoiceBreakOnDisposalMonth?: boolean;

  @Expose()
  reportDefaultTime?: number;

  @Expose()
  reportDefaultWeekDay?: string;

  @Expose()
  reportDefaultTruckCompanyId?: string;

  @Expose()
  reportDefaultOperatorId?: string;

  @Expose()
  reportDefaultTransportRateModelId?: string;

  @Expose()
  reportDefaultDisposalRateModelId?: string;

  @Expose()
  reportDefaultReportTime?: string;

  @Expose()
  reportDefaultReportTimeField?: string;

  @Expose()
  reportDefaultSortByPickupFreightType?: boolean;

  @Expose()
  reportDefaultSortByDeliveryFreightType?: boolean;

  @Expose()
  reportDefaultLockDatabaseTransactions?: boolean;

  @Expose()
  reportDefaultPrimaryCompanyReport?: boolean;

  @Expose()
  reportDefaultReportConfig?: string;

  @Expose()
  codingDefaultAuthorizerId?: string;

  @Expose()
  codingDefaultEngineerId?: string;

  @Expose()
  codingAllowWaterTypeOverrideAfe?: boolean;

  @Expose()
  codingAllowSplit?: boolean;

  @Expose()
  codingAutoCode?: boolean;

  @Expose()
  productionAllocationType?: string;

  @Expose()
  productionAllocationMethod?: string;

  @Expose()
  productionAllocationWeekDay?: string;

  @Expose()
  productionAllocationRetroChunkCount?: number;

  @Expose()
  productionAllocationRetroTimeGroup?: string;

  @Expose()
  activityPeriodOperatorCodedValidation?: boolean;

  @Expose()
  activityPeriodRequireOperatorValidated?: boolean;

  @Expose()
  scadaQuantityAutoChangeEnabled?: boolean;

  @Expose()
  scadaQuantityAutoChangeScadaDiffThreshold?: number;

  @Expose()
  scadaQuantityAutoChangeQuantityDiffThreshold?: number;

  @Expose()
  scadaQuantityAutoChangeOverrideThreshold?: number;

  @Expose()
  serviceEventMinGeofenceEventDuration?: number;

  @Expose()
  dispatchCarryoverEnabled?: boolean;

  @Expose()
  dispatchCarryoverAfterHours?: number;

  @Expose()
  dispatchCarryoverMax?: number;

  @Expose()
  dispatchCarryoverExcludedDefault?: boolean;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'OBJECT_PREF';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for ObjectPreferenceEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ObjectPreference): ObjectPreferenceEntity {
    return plainToInstance(ObjectPreferenceEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ObjectPreference> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ObjectPreferenceCodec, object);
  }

  asTypeValue(): ObjectPreference {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ObjectPreferenceCodec, object);
  }
}
